import type {IssueEventInfo} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {CommitChecksLink} from './CommitChecksLink'
import {MergeQueueLink} from './MergeQueueLink'

type RemovalReasonText = {connector: 'due to' | 'because'; reason: string}

// Mirrors MergeQueues::Entry::RemovalReason and the copy in
// PullRequests::TimelineEvents::MergeQueueEventComponent#event_description.
const UNKNOWN_REASON: RemovalReasonText = {connector: 'due to', reason: 'an unknown reason'}

const REMOVAL_REASONS: Partial<Record<string, RemovalReasonText>> = {
  already_merged: {connector: 'due to', reason: 'it being already merged'},
  checks_timed_out: {connector: 'due to', reason: 'no response for status checks'},
  failed_checks: {connector: 'due to', reason: 'failed status checks'},
  merge_conflict: {connector: 'due to', reason: 'a conflict with the base branch'},
  merged: {connector: 'due to', reason: 'the pull request being merged'},
  manual: {connector: 'due to', reason: 'a manual request'},
  git_tree_invalid: {connector: 'due to', reason: 'an unknown Git error'},
  unknown: UNKNOWN_REASON,
  queue_cleared: {connector: 'due to', reason: 'the queue being cleared'},
  roll_back: {connector: 'due to', reason: 'a roll back'},
  invalid_merge_commit: {connector: 'due to', reason: 'invalid changes in the merge commit'},
  branch_protection_failure: {connector: 'due to', reason: 'Branch Protection failures'},
  stack_invalidated: {connector: 'because', reason: 'a pull request earlier in the stack was removed'},
  stack_parent_unmerged: {
    connector: 'because',
    reason: 'a pull request earlier in the stack was unmerged and was not in the queue',
  },
  stack_out_of_order: {
    connector: 'because',
    reason: 'the pull requests in the stack were not ordered correctly in the queue',
  },
}

// Legacy serialized values accepted by RemovalReason.deserialize.
const LEGACY_REASON_ALIASES: Partial<Record<string, string>> = {
  merge: 'merged',
  ci_failure: 'failed_checks',
  queue_clear: 'queue_cleared',
}

export function removalReasonText(removalReason: string | undefined): RemovalReasonText {
  const key = removalReason ? (LEGACY_REASON_ALIASES[removalReason] ?? removalReason) : 'unknown'
  return REMOVAL_REASONS[key] ?? UNKNOWN_REASON
}

export function RemovedFromMergeQueueText({info}: {info: IssueEventInfo}) {
  const {mergeQueueUrl, removalReason, checksCommitOid} = info
  const {connector, reason} = removalReasonText(removalReason)
  return (
    <>
      {'removed this pull request from the '}
      <MergeQueueLink mergeQueueUrl={mergeQueueUrl} />
      {` ${connector} `}
      {checksCommitOid ? <CommitChecksLink commitOid={checksCommitOid}>{reason}</CommitChecksLink> : reason}
    </>
  )
}
